// RuleSetEditor — an AutoForm over the working RuleSet (flags / scoring / points). Editing any field
// patches the store, which bumps `revision` so every other panel re-evaluates (save→reflect). The
// 青天井 (aotenjou / no-limit) toggle GREYS OUT the score-cap fields (kazoe cap, kiriage mangan,
// double/triple yakuman) because they are meaningless when scores are uncapped (spec §6.1).
import type { ReactNode } from 'react';
import { effectiveLimitMode } from '@mj/engine';
import { useStudioStore, useStudioApi } from '../useStore';
import { RULESET_PRESETS, buildPreset } from '../presets';

function Row({ label, children, dim }: { label: string; children: ReactNode; dim?: boolean }) {
  return (
    <label
      style={{
        display: 'grid',
        gridTemplateColumns: '220px 1fr',
        alignItems: 'center',
        gap: 8,
        padding: '3px 0',
        opacity: dim ? 0.4 : 1,
      }}
    >
      <span style={{ fontSize: 13 }}>{label}</span>
      {children}
    </label>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset style={{ border: '1px solid #ddd', borderRadius: 6, padding: 12, marginBottom: 12 }}>
      <legend style={{ fontWeight: 700, fontSize: 13 }}>{title}</legend>
      {children}
    </fieldset>
  );
}

// Well-known [keeper, keptAlso] exclusion-override pairs surfaced as preset checkboxes. Checked =
// the pair is present in scoring.exclusionOverrides (so keptAlso compounds with keeper instead of
// being dropped via the .excludes graph). Keep this list short and additive.
const EXCLUSION_PRESETS: { keeper: string; keptAlso: string; label: string }[] = [
  { keeper: 'ryanpeikou', keptAlso: 'iipeikou', label: '二盃口×一盃口 (compound)' },
  { keeper: 'suuankou', keptAlso: 'sanankou', label: '四暗刻×三暗刻 (compound)' },
];
function hasOverride(
  list: readonly (readonly [string, string])[] | undefined,
  keeper: string,
  keptAlso: string,
): boolean {
  return !!list?.some(([k, ka]) => k === keeper && ka === keptAlso);
}

// The seven honor tiles, in canonical (hash-stable) order. The wall includes one block of 4 per CHECKED
// id; the toggle below writes the filtered list back so a removed honor's yakuhai becomes unmakeable.
type HonorId = 'E' | 'S' | 'W' | 'N' | 'P' | 'F' | 'C';
const HONOR_OPTIONS: { id: HonorId; label: string }[] = [
  { id: 'E', label: '東' }, { id: 'S', label: '南' }, { id: 'W', label: '西' }, { id: 'N', label: '北' },
  { id: 'P', label: '白' }, { id: 'F', label: '發' }, { id: 'C', label: '中' },
];

export function RuleSetEditor() {
  const rs = useStudioStore((s) => s.ruleSet);
  const api = useStudioApi();
  const { patchScoring, patchFlags, patchPoints, patchTiles, setRuleSet } = api.getState();

  const mode = effectiveLimitMode(rs);
  const uncapped = mode !== 'capped'; // aotenjou OR stepped have no kazoe cap

  return (
    <div style={{ maxWidth: 720 }}>
      <Section title="プリセット / Presets">
        <Row label="ルールを一括選択">
          <select
            defaultValue=""
            onChange={(e) => {
              const next = buildPreset(e.target.value);
              if (next) setRuleSet(next);
              e.currentTarget.value = '';
            }}
          >
            <option value="" disabled>— プリセットを選んで適用 —</option>
            {RULESET_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>{p.name.ja} / {p.name.en}</option>
            ))}
          </select>
        </Row>
        <div style={{ fontSize: 11, color: '#777', paddingLeft: 0 }}>
          適用後も各項目は自由に微調整できます。最後に「Save config」で保存。
        </div>
      </Section>

      <Section title="Tiles">
        <Row label="花牌 (flowers / 春夏秋冬・梅蘭菊竹)">
          <input
            type="checkbox"
            checked={rs.tiles.flowers === true}
            onChange={(e) => patchTiles({ flowers: e.target.checked })}
          />
        </Row>
        <div style={{ fontSize: 11, color: '#777' }}>
          花牌は8枚のボーナス牌。引くと手牌に入らず脇に置かれ、1枚につき+1ドラ＆嶺上から補充されます（北抜きと同様）。
        </div>
        <Row label="使用する字牌 (honors)">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {HONOR_OPTIONS.map((o) => (
              <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={rs.tiles.honors.includes(o.id)}
                  onChange={(e) => {
                    const set = new Set<HonorId>(rs.tiles.honors as HonorId[]);
                    if (e.target.checked) set.add(o.id);
                    else set.delete(o.id);
                    // write back in canonical order so the ruleset hash is independent of click order.
                    patchTiles({ honors: HONOR_OPTIONS.filter((h) => set.has(h.id)).map((h) => h.id) });
                  }}
                />
                {o.label}
              </label>
            ))}
          </div>
        </Row>
        <div style={{ fontSize: 11, color: '#777' }}>
          山に入れる字牌（風牌 東南西北 / 三元牌 白發中）。外すと山から消え、その役牌も作れなくなります。
        </div>
        <Row label="客風牌の除外 (人数連動)">
          <input
            type="checkbox"
            checked={rs.tiles.dropGuestWinds === true}
            onChange={(e) => patchTiles({ dropGuestWinds: e.target.checked })}
          />
        </Row>
        <div style={{ fontSize: 11, color: '#777' }}>
          ON で、その人数の席風にならない風牌を自動で山から除外します（1人=東 / 2人=東南 / 3人=東南西 / 4人=全部）。
          北抜き等の抜きドラ対象になっている風は残ります。三元牌は影響を受けません。OFF（既定）は全字牌そのまま。
        </div>
      </Section>

      <Section title="Scoring">
        <Row label="上限モード (limit mode)">
          <select
            value={mode}
            onChange={(e) => {
              const m = e.target.value as 'capped' | 'aotenjou' | 'stepped';
              // normalize the legacy boolean so a persisted ruleset never carries a contradictory pair.
              patchScoring({ limitMode: m, aotenjou: m === 'aotenjou' });
            }}
          >
            <option value="capped">capped (通常・満貫〜役満で頭打ち)</option>
            <option value="aotenjou">aotenjou (青天井・無制限)</option>
            <option value="stepped">stepped (段階青天井・満貫単位で無限)</option>
          </select>
        </Row>
        {/* kiriage is meaningless under pure aotenjou (no rounding to mangan). */}
        <Row label="切り上げ満貫 (kiriage mangan)" dim={mode === 'aotenjou'}>
          <input
            type="checkbox"
            disabled={mode === 'aotenjou'}
            checked={rs.scoring.kiriageMangan === true}
            onChange={(e) => patchScoring({ kiriageMangan: e.target.checked })}
          />
        </Row>
        {/* kazoe cap only applies under capped. */}
        <Row label="数え役満上限 (kazoe cap han)" dim={uncapped}>
          <input
            type="number"
            disabled={uncapped}
            value={rs.scoring.kazoeYakumanCap}
            min={5}
            max={26}
            onChange={(e) => patchScoring({ kazoeYakumanCap: Number(e.target.value) })}
            style={{ width: 80 }}
          />
        </Row>
        <Row label="ダブル役満 (double yakuman)">
          <input
            type="checkbox"
            checked={rs.scoring.doubleYakuman === true}
            onChange={(e) => patchScoring({ doubleYakuman: e.target.checked })}
          />
        </Row>
        <Row label="トリプル役満 (triple yakuman)">
          <input
            type="checkbox"
            checked={rs.scoring.tripleYakuman === true}
            onChange={(e) => patchScoring({ tripleYakuman: e.target.checked })}
          />
        </Row>
        <Row label="符の丸め (fu rounding)">
          <select
            value={rs.scoring.fuRounding ?? 10}
            onChange={(e) => patchScoring({ fuRounding: Number(e.target.value) as 10 | 1 })}
          >
            <option value={10}>10 (standard)</option>
            <option value={1}>1 (no rounding)</option>
          </select>
        </Row>
        <Row label="裏ドラ (ura-dora)">
          <input
            type="checkbox"
            checked={rs.scoring.uraDora !== false}
            onChange={(e) => patchScoring({ uraDora: e.target.checked })}
          />
        </Row>
        <Row label="槓ドラ (kan-dora)">
          <input
            type="checkbox"
            checked={rs.scoring.kanDora !== false}
            onChange={(e) => patchScoring({ kanDora: e.target.checked })}
          />
        </Row>
        <Row label="翻縛り (han requirement)">
          <select
            value={rs.scoring.hanRequirement ?? 1}
            onChange={(e) => patchScoring({ hanRequirement: Number(e.target.value) as 1 | 2 | 4 })}
          >
            <option value={1}>1 (通常)</option>
            <option value={2}>2 (二翻縛り)</option>
            <option value={4}>4 (四翻縛り)</option>
          </select>
        </Row>
        <Row label="連風牌の雀頭符 (double-wind pair fu)">
          <select
            value={rs.scoring.doubleWindPairFu ?? 4}
            onChange={(e) => patchScoring({ doubleWindPairFu: Number(e.target.value) as 2 | 4 })}
          >
            <option value={4}>4符 (場風+自風)</option>
            <option value={2}>2符</option>
          </select>
        </Row>
        <Row label="連風牌の翻 (double-wind triplet han)">
          <select
            value={rs.scoring.doubleWindTripletHan ?? 2}
            onChange={(e) => patchScoring({ doubleWindTripletHan: Number(e.target.value) as 1 | 2 })}
          >
            <option value={2}>2翻 (場風+自風)</option>
            <option value={1}>1翻 (連風牌は1つ分)</option>
          </select>
        </Row>
        <Row label="ドラ隣 (dora neighbors / 神様麻雀)">
          <input
            type="checkbox"
            checked={rs.scoring.doraNeighbors === true}
            onChange={(e) => patchScoring({ doraNeighbors: e.target.checked })}
          />
        </Row>
        <Row label="役満と通常役の複合 (yakuman stacking)">
          <select
            value={rs.scoring.yakumanStacking ?? 'exclusive'}
            onChange={(e) =>
              patchScoring({ yakumanStacking: e.target.value as 'exclusive' | 'withNormalYaku' })
            }
          >
            <option value="exclusive">exclusive (役満のみ・通常役を無視)</option>
            <option value="withNormalYaku">withNormalYaku (通常役も複合)</option>
          </select>
        </Row>
        {/* exclusionOverrides editor: preset checkboxes toggle well-known [keeper,keptAlso] pairs
            in/out of the array. Checked = the pair compounds instead of being dropped. */}
        <Row label="複合の例外 (exclusion overrides)">
          <div style={{ display: 'grid', gap: 4 }}>
            {EXCLUSION_PRESETS.map((p) => {
              const checked = hasOverride(rs.scoring.exclusionOverrides, p.keeper, p.keptAlso);
              return (
                <label
                  key={`${p.keeper}:${p.keptAlso}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const cur = rs.scoring.exclusionOverrides ?? [];
                      const without = cur.filter(
                        ([k, ka]) => !(k === p.keeper && ka === p.keptAlso),
                      );
                      const next = e.target.checked
                        ? [...without, [p.keeper, p.keptAlso] as [string, string]]
                        : without;
                      patchScoring({ exclusionOverrides: next });
                    }}
                  />
                  {p.label}
                </label>
              );
            })}
          </div>
        </Row>
      </Section>

      <Section title="Flags">
        <Row label="オープン立直 (open riichi)">
          <input type="checkbox" checked={rs.flags.openRiichi} onChange={(e) => patchFlags({ openRiichi: e.target.checked })} />
        </Row>
        <Row label="喰いタン (kuitan)">
          <input type="checkbox" checked={rs.flags.kuitan} onChange={(e) => patchFlags({ kuitan: e.target.checked })} />
        </Row>
        <Row label="流し満貫 (nagashi mangan)">
          <input type="checkbox" checked={rs.flags.nagashiMangan} onChange={(e) => patchFlags({ nagashiMangan: e.target.checked })} />
        </Row>
        <Row label="トビ終了 (tobi / bust ends game)">
          <input type="checkbox" checked={rs.flags.tobi !== false} onChange={(e) => patchFlags({ tobi: e.target.checked })} />
        </Row>
        <Row label="鳴き禁止 (メンゼン強制ルール)">
          <input type="checkbox" checked={rs.flags.noCalls === true} onChange={(e) => patchFlags({ noCalls: e.target.checked })} />
        </Row>
        <Row label="自元（5〜7人の席ドラゴン +1翻）">
          <input type="checkbox" checked={rs.flags.seatDragon === true} onChange={(e) => patchFlags({ seatDragon: e.target.checked })} />
        </Row>
        <Row label="点棒なし立直 (riichi below stick)">
          <input type="checkbox" checked={rs.flags.riichiBelowStick === true} onChange={(e) => patchFlags({ riichiBelowStick: e.target.checked })} />
        </Row>
        <Row label="再立直 (re-riichi)">
          <input type="checkbox" checked={rs.flags.reRiichi === true} onChange={(e) => patchFlags({ reRiichi: e.target.checked })} />
        </Row>
        <Row label="無限立直 (infinite riichi)">
          <input type="checkbox" checked={rs.flags.infiniteRiichi === true} onChange={(e) => patchFlags({ infiniteRiichi: e.target.checked })} />
        </Row>
        <Row label="多牌和了 (multi-win policy)">
          <select value={rs.flags.multiWinPolicy} onChange={(e) => patchFlags({ multiWinPolicy: e.target.value as typeof rs.flags.multiWinPolicy })}>
            <option value="headBump">headBump (頭ハネ)</option>
            <option value="doubleRon">doubleRon (ダブロン)</option>
            <option value="tripleRon">tripleRon (トリロン)</option>
          </select>
        </Row>
        <Row label="連荘 (renchan)">
          <select value={rs.flags.renchan} onChange={(e) => patchFlags({ renchan: e.target.value as typeof rs.flags.renchan })}>
            <option value="agari">agari (和了連荘)</option>
            <option value="tenpai">tenpai (テンパイ連荘)</option>
            <option value="none">none</option>
          </select>
        </Row>
        <Row label="喰い替え (kuikae)">
          <select value={rs.flags.kuikae} onChange={(e) => patchFlags({ kuikae: e.target.value as typeof rs.flags.kuikae })}>
            <option value="none">none</option>
            <option value="strict">strict</option>
            <option value="loose">loose</option>
          </select>
        </Row>
      </Section>

      <Section title="Points">
        <Row label="配給原点 (start)">
          <input type="number" value={rs.points.start} onChange={(e) => patchPoints({ start: Number(e.target.value) })} style={{ width: 100 }} />
        </Row>
        <Row label="返し点 (return)">
          <input type="number" value={rs.points.return} onChange={(e) => patchPoints({ return: Number(e.target.value) })} style={{ width: 100 }} />
        </Row>
        <Row label="本場 (honba value)">
          <input type="number" value={rs.points.honbaValue} onChange={(e) => patchPoints({ honbaValue: Number(e.target.value) })} style={{ width: 100 }} />
        </Row>
        <Row label="立直棒 (riichi stick)">
          <input type="number" value={rs.points.riichiStick} onChange={(e) => patchPoints({ riichiStick: Number(e.target.value) })} style={{ width: 100 }} />
        </Row>
      </Section>
    </div>
  );
}
